import { injectable } from 'tsyringe';
import {
    type DirectoryUserRecord,
    type DirectoryUsersPage,
    type DirectoryEligibleConnection,
    type GetApiDirectorySyncStatus200ResponseItemsInner,
    type DirectorySyncRun,
    type TenantDirectorySyncOutcome,
    ResponseError,
} from '@bmai/directory-api-client';
import { directoryClient } from '@/shared/DirectoryApiClient';

export type { DirectoryUserRecord, DirectoryUsersPage, DirectoryEligibleConnection, DirectorySyncRun, TenantDirectorySyncOutcome };
export type DirectorySyncStatusItem = GetApiDirectorySyncStatus200ResponseItemsInner;

export interface DirectoryUsersQuery {
    page: number;
    pageSize: number;
    search?: string;
    status?: string;
    connectionId?: string;
}

@injectable()
export class DirectoryApiService {
    private readonly api = directoryClient.directoryApi;

    async listUsers(query: DirectoryUsersQuery): Promise<DirectoryUsersPage> {
        return runApi('list users', () =>
            this.api.getUsers({
                page: String(query.page),
                pageSize: String(query.pageSize),
                ...(query.search !== undefined && query.search.length > 0 ? { search: query.search } : {}),
                ...(query.status !== undefined && query.status.length > 0 ? { status: query.status } : {}),
                ...(query.connectionId !== undefined && query.connectionId.length > 0 ? { connectionId: query.connectionId } : {}),
            }),
        );
    }

    async getUser(externalId: string, connectionId?: string): Promise<DirectoryUserRecord> {
        return runApi('get user', () =>
            this.api.getUsersByExternalId({ externalId, ...(connectionId !== undefined ? { connectionId } : {}) }),
        );
    }

    async listConnections(): Promise<DirectoryEligibleConnection[]> {
        return runApi('list connections', async () => {
            const { connections } = await this.api.getConnections();
            return connections;
        });
    }

    async getSyncStatus(): Promise<DirectorySyncStatusItem[]> {
        return runApi('sync status', async () => {
            const { items } = await this.api.getSyncStatus();
            return items;
        });
    }

    async syncAll(): Promise<TenantDirectorySyncOutcome> {
        return runApi('sync tenant', () => this.api.postSync());
    }

    async syncConnection(connectionId: string): Promise<DirectorySyncRun> {
        return runApi('sync connection', () => this.api.postConnectionsByConnectionIdSync({ connectionId }));
    }
}

async function runApi<T>(op: string, call: () => Promise<T>): Promise<T> {
    try {
        return await call();
    } catch (cause) {
        if (cause instanceof ResponseError) {
            throw new Error(await readError(cause.response, op));
        }
        throw cause;
    }
}

async function readError(response: Response, op: string): Promise<string> {
    const body = await response
        .clone()
        .json()
        .then((value) => value as { error?: string })
        .catch(() => null);
    if (body && typeof body.error === 'string' && body.error.length > 0) return body.error;
    return `${op} failed (${response.status} ${response.statusText})`;
}
